<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="550"
      overlay-color="rgba(39, 50, 64)"
      overlay-opacity="0.6"
      :fullscreen="$vuetify.breakpoint.xs ? true : false"
      persistent
      :transition="false"
    >
      <v-card>
        <v-card-title
          class="header info"
          primary-title
        >
          <b>{{ $t("rankings.joinAcc") }}</b>
        </v-card-title>
        <v-card-text v-if="!done && !$apollo.loading">
          <v-flex
            v-if="needsNew()"
            xs12
            class="mt-4"
          >
            <div v-if="projectType == 'trading.copy'">
              <b>
                {{ $t('rankings.copyTrading') }}.  {{ $t("rankings.lotSize") }} 1:{{ !risk_ratio ? riskRatio : risk_ratio }} ({{ !risk_ratio ? riskRatio * 100 : risk_ratio * 100 }}%)
              </b>
              <v-tooltip
                v-if="projectType == 'trading.copy'"
                min-width="300px"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    style="margin-top:-3px;font-size:18px;"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                {{ $t("rankings.copyTradingHint") }}
              </v-tooltip>
            </div>
            <div v-else>
              <b>
                {{ $t('rankings.keyTrading') }}
              </b>
              <v-tooltip
                top
                min-width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    style="margin-top:-3px;font-size:18px;"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                {{ $t("rankings.keyTradingHint") }}
              </v-tooltip>
            </div>
            
            <v-select
              v-model.trim="account"
              class="mt-7"
              :items="investorAccounts"
              :item-text="formatName"
              :item-value="'id'"
              :item-disabled="item => item.project !== null"
              :error-messages="SelectionErrors"
              name="account"
              :label="$t('rankings.chooseAcc')"
              required
              @change="checkBalance()"
              @blur="$v.account.$touch()"
            />
          </v-flex>
          <v-flex
            v-else
            xs12
            class="text-center mt-5"
          >
            {{ $t("rankings.dontHaveAccs1") }}
            <b>{{ $t("rankings.dontHaveAccs2") }}</b>
          </v-flex>
          <v-flex v-if="account != null">
            <small>{{ $t('rankings.feeStruct') }}</small>
            <v-radio-group v-model="feeStructure">
              <v-radio
                v-for="(f,i) in fees"
                :key="i"
                :value="f.type"
              >
                <template
                  v-slot:label
                >
                  <small><b>{{ f.text }}</b></small>
                </template>
              </v-radio>
            </v-radio-group>
          </v-flex>
          <v-flex
            v-if="account != null"
          >
            <v-list-item style="padding-left:0; margin-bottom:10px;">
              <v-list-item-action>
                <v-switch
                  v-model="advSettings"
                  color="indigo darken-2"
                />
              </v-list-item-action>
              <v-list-item-title class="font-weight-light">
                {{ $t('rankings.advancedSettings') }}
              </v-list-item-title>
            </v-list-item>
            <span
              v-if="advSettings && !plan.config.risk_management"
              class="info"
              style="padding:10px;margin-top:-10px;margin-bottom:10px;display:block"
            >
              {{ $t('packs.notAvailable') }}. 
              <v-btn
                class="success"
                outlined
                text
                small
                @click="$router.push('/subscriptions')"
              >
                {{ $t('packs.upgrade') }}
              </v-btn>
            </span>
            <v-select
              v-if="advSettings && projectType == 'trading.copy' && plan.config.risk_management"
              v-model.trim="risk_ratio"
              :items="riskRatios"
              :item-text="'text'"
              :item-value="'id'"
              name="risk_ratio"
              :label="$t('rankings.lotSize')"
            >
              <template v-slot:append>
                <v-tooltip
                  top
                  max-width="300"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  {{ $t("rankings.lotSizeHint") }}
                </v-tooltip>
              </template>
            </v-select>
            <v-select
              v-if="advSettings && plan.config.risk_management"
              v-model.trim="max_drawdown"
              :items="maxDrawdown"
              :item-text="'text'"
              :item-value="'id'"
              name="max_drawdown"
              :label="$t('rankings.maxDrawdown')"
            >
              <template v-slot:append>
                <v-tooltip
                  top
                  max-width="300"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  {{ $t("rankings.maxDrawdownHint") }}
                </v-tooltip>
              </template>
            </v-select>
            <v-checkbox
              v-model="acceptTerms"
              color="primary"
            >
              <template v-slot:label>
                <small>
                  <b>{{ $t('rankings.acceptConnectTerms') }}</b>
                  <p
                    v-if="error !== null"
                    class="text-danger"
                  >
                    <small>{{ error }}</small>
                  </p>
                </small>
              </template>
            </v-checkbox>
          </v-flex>
        </v-card-text>

        <v-card-text
          v-if="done && !$apollo.loading"
          class="mt-5 text-center"
        >
          <helper-success-animation class="mb-4" />
          <div v-if="projectType != 'trading.copy'">
            <b>{{ $t("rankings.joinedMsg1") }}</b>
            {{ $t("rankings.joinedMsg2") }}
            <b>{{ $t("rankings.joinedMsg3") }}</b>
            .
            {{ $t("rankings.joinedMsg4") }}
            <b>
              <a
                @click.prevent="
                  dialog = false;
                  $router.push('/accounts');
                "
              >{{ $t("rankings.joinedMsg5") }}</a>
            </b>
          </div>
          <div v-else>
            {{ $t("rankings.copyTradingConnected") }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions v-if="!done && !$apollo.loading">
          <v-spacer />
          <v-btn
            text
            :disabled="loading"
            @click="dialog = false"
          >
            {{ $t("globals.cancelButton") }}
          </v-btn>
          <v-btn
            v-if="needsNew()"
            color="primary"
            :disabled="loading"
            :loading="loading"
            @click="save()"
          >
            {{ $t("rankings.joinButton") }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :disabled="loading"
            @click="editAccountDialog = true"
          >
            {{ $t("globals.newAccButton") }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-spacer />
          <v-btn
            text
            :disabled="loading"
            @click="dialog = false"
          >
            {{ $t("globals.closeButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <edit-account-dialog 
      :show-dialog="editAccountDialog"
      @closeEditAccountDialog="closeEditAccountDialog"
    />
    <helper-upgrade-plan-dialog ref="upgradeDialog" />
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";

import EditAccountDialog from "@/views/Accounts/partial/EditAccountDialog.vue";
import INVESTOR_ACCOUNTS_QUERY from "@/graphql/queries/InvestorAccounts.gql";
import CONNECT_PROJECT_MUTATION from "@/graphql/mutations/ConnectToProject.gql";
import SERVICES_QUERY from '@/graphql/queries/Services.gql';

export default {
  name: "ConnectProject",
  components: {
    EditAccountDialog
  },
  mixins: [validationMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    drawdown: {
      type: Number,
      default: 0
    },
    projectType: {
      type: String,
      default: ""
    },
    riskRatio: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      fees: [
          {type:"success", text: this.$t('rankings.sFeeDesc')},
          {type:"management", text: this.$t('rankings.mFeeDesc')}
      ],
      feeStructure: "success",
      account: null,
      insurance: null,
      risk_ratio: null,
      max_drawdown: null,
      loading: false,
      editAccountDialog: false,
      done: false,
      error: null,
      acceptTerms: false,
      advSettings: false,
    };
  },
  computed: {
    plan() {
      return this.services ? this.services.find(o => o.type == 'plan') : null;
    },
    dialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$parent.connectDialog = val;
      }
    },
    SelectionErrors() {
      const errors = [];
      if (!this.$v.account.$dirty) return errors;
      !this.$v.account.required && errors.push(this.$t("rankings.chooseAcc"));
      return errors;
    },
    riskRatios() {
      let ratios = []
      for (var i = 10; i <= 200; i += 10) {
        ratios.push({'id': i / 100, 'text' : i + '%'})
      } 
     return ratios
    },
    maxDrawdown() {
      let drawdown = []
      for (var i = 10; i <= 100; i += 1) {
        drawdown.push({'id': i, 'text' : i + '%'})
      } 
     return drawdown
    }
  },
  watch: {
    showDialog(n, o) {
      if (n) {
        this.account = null;
        this.$v.$reset();
        this.done = false;
      }
    },
    plan(n, o) {
      if (n.config.linkings && n.config.linkings <= n.usage.linkings) {
        this.showDialog = false
        this.$refs.upgradeDialog.open();
      }
    }
  },
  methods: {
    checkBalance() {
      this.$v.account.$touch()
      const acc_id = this.account
      let acc = _.find(this.investorAccounts, function(acc) {
          return acc.id === acc_id;
      })
    },
    needsNew() {
      if (_.isEmpty(this.investorAccounts)) return false;
      let project = _.mapValues(this.investorAccounts, "project");
      let val = _.indexOf(_.values(project), null);
      return val == -1 ? false : true;
    },
    formatName(item) {
      let name = item.dataroom.exchange.name + " - " + item.name;
      name +=
        item.project !== null ? ` (connected to ${item.project.ticker})` : "";
      return name;
    },
     closeEditAccountDialog(val) {
      this.editAccountDialog = false
    },
    save() {
      this.needsNew();
      this.$v.$touch();

       if (this.projectType == 'trading.copy' && !this.acceptTerms) {
        this.error = this.$t("profile.pleaseAcceptTerms");
        return;
      }
     
      if (!this.$v.$invalid) {
        this.loading = true;

        let params = {
          investor_account_id: this.account,
          project_id: this.$parent.ranking.project.id,
          insurance: 0,
          fee_type: this.feeStructure
        };
        if (this.risk_ratio != null) {
          params.risk_ratio = this.risk_ratio
        } 
        if (this.max_drawdown != null) {
          params.drawdown = this.max_drawdown
        } 

        this.$apollo
          .mutate({
            mutation: CONNECT_PROJECT_MUTATION,
            variables: params
          })
          .then(data => {
            this.loading = false;
            this.$parent.$apollo.queries.ranking.refetch();
            this.$apollo.queries.investorAccounts.refetch();
            this.done = true;
          })
          .catch(error => {
            this.errorHandler(error);
            this.loading = false;
          });
      }
    }
  },
  validations: {
    account: {
      required
    },
  },
  apollo: {
    investorAccounts: {
      query: INVESTOR_ACCOUNTS_QUERY,
      skip() {
        return localStorage.getItem("auth-token") == null;
      }
    },
    services: {
      query: SERVICES_QUERY,
    }
  }
};
</script>

<style></style>
